import React, { useState, useEffect } from 'react';
import AdminNavBar from '../../components/AdminNavBar';
import supabase from '../../utils/supabaseClient';
import './styles.css';

function UploadFamiliaThumbnails() {
  const [familias, setFamilias] = useState([]);
  const [selectedFamilia, setSelectedFamilia] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingThumbnails, setExistingThumbnails] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    fetchFamilias();
  }, []);

  useEffect(() => {
    if (selectedFamilia) {
      fetchExistingThumbnails();
    } else {
      setExistingThumbnails([]);
    }
  }, [selectedFamilia]);

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      setUser(user);
    } catch (error) {
      console.error('Error checking auth:', error);
    }
  };

  const fetchFamilias = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('familia')
        .select('id, nombre, number')
        .order('number', { ascending: true });

      if (error) throw error;
      setFamilias(data || []);
    } catch (error) {
      console.error('Error fetching familias:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingThumbnails = async () => {
    if (!selectedFamilia) return;

    try {
      const { data, error } = await supabase
        .from('familia_thumbnail')
        .select('*')
        .eq('familia_id', selectedFamilia);

      if (error) throw error;
      setExistingThumbnails(data || []);
    } catch (error) {
      console.error('Error fetching existing thumbnails:', error);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const maxFiles = 3;

    if (files.length > maxFiles) {
      showToast(`Solo puedes subir hasta ${maxFiles} imágenes`, 'error');
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      showToast('Solo se permiten archivos JPG, PNG y WebP', 'error');
      return;
    }

    setSelectedFiles(files);

    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    document.getElementById('familia-thumbnail-input').value = '';
  };

  const deleteExistingThumbnail = async (thumbnailId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('¿Estás seguro de que quieres eliminar esta imagen?')) return;

    try {
      const { error } = await supabase
        .from('familia_thumbnail')
        .delete()
        .eq('familia_id', selectedFamilia)
        .eq('id', thumbnailId);

      if (error) throw error;

      showToast('Imagen eliminada correctamente', 'success');
      fetchExistingThumbnails();
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
      showToast('Error al eliminar la imagen', 'error');
    }
  };

  const deleteAllThumbnails = async () => {
    if (!selectedFamilia) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('¿Estás seguro de que quieres eliminar TODAS las imágenes de esta familia?')) return;

    try {
      const { error } = await supabase
        .from('familia_thumbnail')
        .delete()
        .eq('familia_id', selectedFamilia);

      if (error) throw error;

      showToast('Todas las imágenes eliminadas correctamente', 'success');
      fetchExistingThumbnails();
    } catch (error) {
      console.error('Error deleting all thumbnails:', error);
      showToast('Error al eliminar las imágenes', 'error');
    }
  };

  const uploadThumbnails = async () => {
    if (!selectedFamilia || selectedFiles.length === 0) {
      showToast('Selecciona una familia y al menos una imagen', 'error');
      return;
    }

    if (!user) {
      showToast('Debes iniciar sesión para subir imágenes', 'error');
      return;
    }

    // Check if adding these files would exceed the limit
    const totalAfterUpload = existingThumbnails.length + selectedFiles.length;
    if (totalAfterUpload > 3) {
      showToast(`Solo puedes tener hasta 3 imágenes por familia. Actualmente tienes ${existingThumbnails.length}. Elimina algunas antes de subir más.`, 'error');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const file of selectedFiles) {
        try {
          // Upload file to storage
          const fileExt = file.name.split('.').pop();
          const fileName = `familia_${selectedFamilia}_${Date.now()}.${fileExt}`;
          const filePath = `familia_thumbnails/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('resources')
            .upload(filePath, file);

          if (uploadError) {
            console.error('Error uploading file:', uploadError);
            errorCount++;
            continue;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('resources')
            .getPublicUrl(filePath);

          // Add record to familia_thumbnail table
          const { error: dbError } = await supabase
            .from('familia_thumbnail')
            .insert([{
              familia_id: parseInt(selectedFamilia),
              url: publicUrl
            }]);

          if (dbError) {
            console.error('Error saving to database:', dbError);
            errorCount++;
          } else {
            successCount++;
          }
        } catch (fileError) {
          console.error(`Error processing ${file.name}:`, fileError);
          errorCount++;
        }
      }

      // Clear form and refresh thumbnails
      clearSelection();
      fetchExistingThumbnails();

      // Show results
      if (successCount > 0 && errorCount === 0) {
        showToast(`${successCount} imagen(es) subidas exitosamente!`, 'success');
      } else if (successCount > 0 && errorCount > 0) {
        showToast(`${successCount} imagen(es) subidas exitosamente, ${errorCount} fallaron.`, 'warning');
      } else {
        showToast('Error al subir las imágenes', 'error');
      }
    } catch (error) {
      console.error('Error uploading thumbnails:', error);
      showToast('Error al subir las imágenes', 'error');
    } finally {
      setUploading(false);
    }
  };

  const showToast = (message, type) => {
    // Simple toast implementation
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 24px;
      border-radius: 6px;
      color: white;
      font-weight: 500;
      z-index: 10000;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#f59e0b'};
    `;
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  if (!user) {
    return (
      <div>
        <AdminNavBar />
        <div className="admin-container">
          <div className="admin-content">
            <p>Debes iniciar sesión para acceder a esta página.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminNavBar />
      <div className="admin-container">
        <div className="admin-content">
          <h1>Gestionar Imágenes de Familias</h1>
          <p>Sube hasta 3 imágenes por familia para crear un carrusel automático</p>

          {loading ? (
            <div className="loading">Cargando familias...</div>
          ) : (
            <div className="upload-section">
              {/* Familia Selection */}
              <div className="form-group">
                <label htmlFor="familia-select">Seleccionar Familia:</label>
                <select
                  id="familia-select"
                  value={selectedFamilia}
                  onChange={(e) => setSelectedFamilia(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Selecciona una familia --</option>
                  {familias.map(familia => (
                    <option key={familia.id} value={familia.id}>
                      {familia.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {selectedFamilia && (
                <>
                  {/* Existing Thumbnails */}
                  <div className="existing-thumbnails">
                    <div className="section-header">
                      <h3>Imágenes Actuales ({existingThumbnails.length}/3)</h3>
                      {existingThumbnails.length > 0 && (
                        <button
                          onClick={deleteAllThumbnails}
                          className="btn btn-danger btn-sm"
                        >
                          Eliminar Todas
                        </button>
                      )}
                    </div>
                    
                    {existingThumbnails.length === 0 ? (
                      <p className="no-images">No hay imágenes para esta familia</p>
                    ) : (
                      <div className="thumbnail-grid">
                        {existingThumbnails.map((thumbnail, index) => (
                          <div key={thumbnail.id} className="thumbnail-item">
                            <img
                              src={thumbnail.url}
                              alt={`Thumbnail ${index + 1}`}
                              className="thumbnail-preview"
                            />
                            <button
                              onClick={() => deleteExistingThumbnail(thumbnail.id)}
                              className="delete-btn"
                              title="Eliminar imagen"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Upload Section */}
                  {existingThumbnails.length < 3 && (
                    <div className="upload-new">
                      <h3>Subir Nuevas Imágenes</h3>
                      <p>Puedes subir {3 - existingThumbnails.length} imagen(es) más</p>
                      
                      <div className="form-group">
                        <label htmlFor="familia-thumbnail-input">Seleccionar Imágenes:</label>
                        <input
                          type="file"
                          id="familia-thumbnail-input"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={handleFileChange}
                          className="form-control"
                        />
                        <small className="form-text">
                          Formatos soportados: JPG, PNG, WebP. Máximo 3 imágenes por familia.
                        </small>
                      </div>

                      {/* Preview Selected Files */}
                      {previewUrls.length > 0 && (
                        <div className="preview-section">
                          <h4>Vista Previa:</h4>
                          <div className="preview-grid">
                            {previewUrls.map((url, index) => (
                              <div key={index} className="preview-item">
                                <img
                                  src={url}
                                  alt={`Preview ${index + 1}`}
                                  className="preview-image"
                                />
                              </div>
                            ))}
                          </div>
                          <div className="preview-actions">
                            <button
                              onClick={clearSelection}
                              className="btn btn-secondary"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={uploadThumbnails}
                              disabled={uploading}
                              className="btn btn-primary"
                            >
                              {uploading ? 'Subiendo...' : 'Subir Imágenes'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {existingThumbnails.length === 3 && (
                    <div className="limit-reached">
                      <p><strong>Límite alcanzado:</strong> Esta familia ya tiene el máximo de 3 imágenes.</p>
                      <p>Elimina alguna imagen existente si quieres subir una nueva.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UploadFamiliaThumbnails;